/**
 * Datasource remoto Clients: abstração + implementação Firestore.
 * Clients é subcoleção de users: users/{userId}/clients/{clientId}.
 * Recebe Firestore por injeção (clean architecture); se não passado, usa getFirestoreDb().
 */

import type { Firestore } from 'firebase/firestore';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  addDoc,
} from 'firebase/firestore';
import { getFirestoreDb } from '@/core/services';
import { Client, ClientRef, clientSchema, UserRef } from '@/core/domain';

export interface ClientRemoteDataSource {
  getById(userId: string, id: string): Promise<Client | null>;
  getAllByUserId(userId: string): Promise<Client[]>;
  create(userId: string, client: Client): Promise<Client>;
  update(userId: string, id: string, client: Partial<Client>): Promise<Client>;
  delete(userId: string, id: string): Promise<void>;
}

function toClient(id: string, data: Record<string, unknown>): Client {
  const { id: _ignoredIdFromFirestore, ...rest } = data;
  const parsed = clientSchema.safeParse({ ...rest, id });
  if (!parsed.success) {
    console.error('[ClientFirestoreDataSource] Invalid client from Firestore', {
      clientId: id,
      issues: parsed.error.issues.map((issue) => ({
        path: issue.path.join('.'),
        message: issue.message,
        code: issue.code,
      })),
      rawData: data,
    });
    throw new Error('Invalid client from Firestore');
  }
  return parsed.data;
}

export class ClientFirestoreDataSource implements ClientRemoteDataSource {
  constructor(private readonly db?: Firestore) {}

  private getDb(): Firestore {
    return this.db ?? getFirestoreDb();
  }

  private clientDocRef(userId: string, id: string) {
    return doc(this.getDb(), UserRef.collection, userId, ClientRef.collection, id);
  }

  private clientsCollectionRef(userId: string) {
    return collection(this.getDb(), UserRef.collection, userId, ClientRef.collection);
  }

  async getById(userId: string, id: string): Promise<Client | null> {
    const ref = this.clientDocRef(userId, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return toClient(snap.id, snap.data() as Record<string, unknown>);
  }

  async getAllByUserId(userId: string): Promise<Client[]> {
    const ref = this.clientsCollectionRef(userId);
    const snap = await getDocs(ref);
    return snap.docs.map((d) => toClient(d.id, d.data() as Record<string, unknown>));
  }

  async create(userId: string, client: Client): Promise<Client> {
    const ref = this.clientsCollectionRef(userId);
    const { id: _ignoredIdFromInput, ...clientPayload } = client;
    const docRef = await addDoc(ref, clientPayload);
    const id = docRef.id;
    await setDoc(docRef, { id }, { merge: true });
    return toClient(id, { ...client, id });
  }

  async update(userId: string, id: string, client: Partial<Client>): Promise<Client> {
    const ref = this.clientDocRef(userId, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Client not found');
    const current = toClient(snap.id, snap.data() as Record<string, unknown>);
    const updated = { ...current, ...client, id };
    await setDoc(ref, updated);
    return updated;
  }

  async delete(userId: string, id: string): Promise<void> {
    const ref = this.clientDocRef(userId, id);
    await deleteDoc(ref);
  }
}
